// Lightweight, env-driven Google Analytics (GA4) + Google Ads conversion tracking.
// No-ops safely until you set REACT_APP_GA_ID and/or REACT_APP_GOOGLE_ADS_ID in frontend/.env.
const GA_ID = process.env.REACT_APP_GA_ID;
const ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID;
const ADS_LABEL = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_LABEL;

let initialized = false;

export function initAnalytics() {
  if (initialized) return;
  const primaryId = GA_ID || ADS_ID;
  if (!primaryId || typeof document === "undefined") return; // not configured -> no-op

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${primaryId}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  if (GA_ID) window.gtag("config", GA_ID);
  if (ADS_ID) window.gtag("config", ADS_ID);
  initialized = true;
}

export function trackEvent(name, params = {}) {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", name, params);
  }
}

// Fire on "Start Free" CTAs and successful signups so Google Ads can optimize conversions.
export function trackStartFreeConversion(source = "cta") {
  trackEvent("start_free_click", { source });
  logEvent("cta_click", { source });
  if (typeof window !== "undefined" && typeof window.gtag === "function" && ADS_ID && ADS_LABEL) {
    window.gtag("event", "conversion", { send_to: `${ADS_ID}/${ADS_LABEL}` });
  }
}

// Session id for first-touch funnel attribution (no PII).
function sessionId() {
  if (typeof window === "undefined") return null;
  try {
    let s = localStorage.getItem("ud_sid");
    if (!s) {
      s = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now()) + Math.random().toString(16).slice(2);
      localStorage.setItem("ud_sid", s);
    }
    return s;
  } catch {
    return null;
  }
}

// Fires an event only the FIRST time per user (guarded via localStorage).
// Used for activation milestones like first_checkin_completed. Keyed by userKey
// (user id) so distinct users on the same device each get their own "first".
export function logFirstEvent(name, userKey, props = {}) {
  try {
    const key = `ud_first_${userKey || "anon"}_${name}`;
    if (typeof localStorage !== "undefined") {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, String(Date.now()));
    }
  } catch {
    /* storage blocked — fall through and still fire once this session */
  }
  logEvent(name, { ...props, first: true });
}

// Server-side funnel event (fire-and-forget). Also mirrors to gtag.
export function logEvent(name, props = {}) {
  trackEvent(name, props);
  const base = process.env.REACT_APP_BACKEND_URL;
  if (!base || typeof fetch === "undefined") return;
  try {
    fetch(`${base}/api/events`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, props, session_id: sessionId() }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* no-op */
  }
}
